// Lazy Loading Manager for Heavy Libraries
window.LazyLoader = {
  loaded: {},
  loading: {},

  // Load a script dynamically
  loadScript(src, id) {
    if (this.loaded[id]) {
      return Promise.resolve();
    }

    if (this.loading[id]) {
      return this.loading[id];
    }

    this.loading[id] = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        this.loaded[id] = true;
        delete this.loading[id];
        resolve();
      };
      script.onerror = () => {
        delete this.loading[id];
        reject(new Error(`Failed to load ${src}`));
      };
      document.body.appendChild(script);
    });

    return this.loading[id];
  },

  // Load CSS dynamically
  loadCSS(href, id) {
    if (this.loaded[id]) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = () => {
        this.loaded[id] = true;
        resolve();
      };
      document.head.appendChild(link);
    });
  },

  // Load Plyr video player when needed
  async loadPlyr() {
    if (typeof Plyr !== 'undefined') return;
    await this.loadScript(theme.assets.plyr, 'plyr');
  },

  // Load PhotoSwipe when needed
  async loadPhotoSwipe() {
    if (window.PhotoSwipe) return;
    await Promise.all([
      this.loadScript(theme.assets.photoswipe || '/assets/photoswipe-v5.3.2.esm.min.js', 'photoswipe'),
      this.loadScript('/assets/photoswipe-lightbox-v5.3.2.esm.min.js', 'photoswipe-lightbox')
    ]);
  },

  // Load Masonry when needed
  async loadMasonry() {
    if (typeof Masonry !== 'undefined') return;
    await this.loadScript(theme.assets.masonry, 'masonry');
  },

  // Load Lottie animations when needed
  async loadLottie() {
    if (typeof lottie !== 'undefined') return;
    await this.loadScript('/assets/lottie.lib.js', 'lottie');
  },

  // Load ScrollReveal when needed
  async loadScrollReveal() {
    if (typeof ScrollReveal !== 'undefined') return;
    await this.loadScript('/assets/scrollreveal.min.js', 'scrollreveal');
  },

  // Initialize lazy loading for elements
  init() {
    // Skip video lazy loading - now handled by immediate loading in theme.liquid
    // Videos load immediately when detected on page for better UX

    // Lazy load image galleries
    const galleryElements = document.querySelectorAll('[data-photoswipe], .product-gallery');
    if (galleryElements.length > 0) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadPhotoSwipe().then(() => {
              // Initialize PhotoSwipe after loading
              if (window.initializePhotoSwipe) {
                window.initializePhotoSwipe(entry.target);
              }
            });
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });

      galleryElements.forEach(el => observer.observe(el));
    }

    // Lazy load masonry layouts
    const masonryElements = document.querySelectorAll('[data-masonry], .masonry-grid');
    if (masonryElements.length > 0) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadMasonry().then(() => {
              // Initialize Masonry after loading
              if (window.Masonry) {
                new Masonry(entry.target);
              }
            });
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });

      masonryElements.forEach(el => observer.observe(el));
    }

    // Lazy load animations
    const animationElements = document.querySelectorAll('[data-lottie], .lottie-animation');
    if (animationElements.length > 0) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadLottie();
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '200px' });

      animationElements.forEach(el => observer.observe(el));
    }

    // Lazy load scroll reveal animations if enabled
    if (document.querySelector('[data-scroll-reveal]')) {
      this.loadScrollReveal().then(() => {
        if (window.ScrollReveal) {
          ScrollReveal().reveal('[data-scroll-reveal]');
        }
      });
    }
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => LazyLoader.init());
} else {
  LazyLoader.init();
}